import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { callFn } from '../lib/firebase';
import { sendEncryptedMessage, sendEncryptedAttachment, getMyWhitelist } from '../lib/messaging';
import { sendFsMessage, sendFsAttachment } from '../lib/fsMessaging';

const SIGIL_RE = /^[a-z0-9_]{3,20}$/i;

const TTL_OPTIONS = [
  { value: 0, label: 'zero-persist (sparisce subito dopo lettura)' },
  { value: 10, label: '10 secondi' },
  { value: 300, label: '5 minuti' },
  { value: 3600, label: '1 ora' },
  { value: 86400, label: '24 ore' },
  { value: -1, label: '∞ — non scade mai (Premium)' },
];

export default function NewChatScreen() {
  const navigate = useNavigate();
  const { sigil: mySigil, boxSec, boxPub, derivedKeys, firebaseUser } = useStore();

  // "Scrivi come": identita' con cui inviare. '' = principale; altrimenti il
  // sigilLower di un derivato. Determina sia senderSigil sia le box keys usate.
  const [asSigil, setAsSigil] = useState('');
  const derivedList = Object.entries(derivedKeys || {}); // [sigilLower, {sigil,boxSec,boxPub}]
  function senderCtx() {
    if (asSigil && derivedKeys?.[asSigil]) {
      const d = derivedKeys[asSigil];
      return { sigil: d.sigil, secKey: d.boxSec, pubKey: d.boxPub };
    }
    return { sigil: mySigil, secKey: boxSec, pubKey: boxPub };
  }

  const [sigilInput, setSigilInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(null);
  const [found, setFound] = useState(null);

  const [text, setText] = useState('');
  const [ttl, setTtl] = useState(10);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachDownloadable, setAttachDownloadable] = useState(false);
  const inputGalleryRef = useRef(null);
  const inputVideoLiveRef = useRef(null);
  const inputAudioLiveRef = useRef(null);
  const inputAnyFileRef = useRef(null);
  const [autoConnect, setAutoConnect] = useState(null); // sigil da invito → cerca subito
  const [contacts, setContacts] = useState(null); // rubrica: contatti pre-approvati

  // Se arrivo qui dopo aver accettato un invito, uso il creator completo
  // (salvato dal redeem) → niente ricerca (i derivati privati non si trovano).
  useEffect(() => {
    let full;
    try { full = sessionStorage.getItem('nullum.connectToFull'); } catch {}
    if (full) {
      try { sessionStorage.removeItem('nullum.connectToFull'); } catch {}
      try { sessionStorage.removeItem('nullum.connectTo'); } catch {}
      try {
        const cr = JSON.parse(full);
        if (cr?.uid && cr?.pubKeyBox && cr?.sigil) {
          setSigilInput(cr.sigil);
          setFound({
            uid: cr.uid,
            sigil: cr.sigil,
            pubKeyBox: cr.pubKeyBox,
            acceptMode: cr.whitelistLevel === 'full' ? 'request' : 'request',
          });
          return;
        }
      } catch {}
    }
    // Fallback legacy: solo il sigil → ricerca.
    let c;
    try { c = sessionStorage.getItem('nullum.connectTo'); } catch {}
    if (c) {
      try { sessionStorage.removeItem('nullum.connectTo'); } catch {}
      setSigilInput(c);
      setAutoConnect(c);
    }
  }, []);

  useEffect(() => {
    if (autoConnect && sigilInput === autoConnect) {
      setAutoConnect(null);
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, sigilInput]);

  // Rubrica: carica i contatti pre-approvati per scrivere con un tap.
  useEffect(() => {
    let alive = true;
    getMyWhitelist(firebaseUser?.uid)
      .then((list) => { if (alive) setContacts((list || []).filter((c) => c.sigil)); })
      .catch(() => { if (alive) setContacts([]); });
    return () => { alive = false; };
  }, [firebaseUser]);

  function pickContact(s) {
    setSearchErr(null);
    setSigilInput(s);
    setAutoConnect(s); // l'effetto sopra fa partire la ricerca da solo
  }

  async function doSearch(e) {
    e?.preventDefault?.();
    setSearchErr(null);
    setFound(null);

    const clean = sigilInput.trim().replace(/^@/, '').toLowerCase();
    if (!SIGIL_RE.test(clean)) {
      setSearchErr('sigil non valido (3-20 char, a-z 0-9 _)');
      return;
    }
    if (clean === mySigil?.toLowerCase()) {
      setSearchErr('non puoi trasmettere a te stesso');
      return;
    }
    setSearching(true);
    try {
      const fn = callFn('findUserBySigil');
      const res = await fn({ sigil: clean });
      const payload = res.data;
      if (!payload?.ok) {
        setSearchErr(`server: ${payload?.error || 'sconosciuto'} (${payload?.stage || '?'})`);
        return;
      }
      if (!payload.found) {
        setSearchErr(`non trovato: ${payload.reason || 'motivo sconosciuto'}`);
        return;
      }
      setFound({
        uid: payload.uid,
        sigil: payload.sigil || clean,
        pubKeyBox: payload.pubKeyBox,
        acceptMode: payload.acceptMode || 'request',
      });
    } catch (err) {
      const code = err?.code || err?.details?.code;
      setSearchErr(`errore di rete: ${err?.message || 'sconosciuto'}${code ? ` (${code})` : ''}`);
    } finally {
      setSearching(false);
    }
  }

  async function doSend() {
    setSendErr(null);
    if (!found) return;
    if (!text.trim()) { setSendErr('messaggio vuoto'); return; }
    if (text.length > 500) { setSendErr('massimo 500 caratteri'); return; }
    if (!boxSec) { setSendErr('chiavi non inizializzate, ricarica'); return; }
    setSending(true);
    try {
      const ctx = senderCtx();
      const sendArgs = {
        text: text.trim(),
        senderTTL: ttl,
        senderSigil: ctx.sigil,           // scrivo come principale o derivato
        recipientSigil: found.sigil,      // chat keyata sul sigil cercato
      };
      // Forward-secret di default; fallback statico se il destinatario non ha
      // ancora pubblicato prekey (client non aggiornato — finestra di rollout).
      let res;
      try {
        res = await sendFsMessage({
          ...sendArgs,
          boxKeypair: { pub: ctx.pubKey, priv: ctx.secKey },
          recipientUid: found.uid,
          myBoxPub: ctx.pubKey,
          mySecKeyBox: ctx.secKey,
        });
      } catch (e) {
        if (!String(e?.message || '').includes('PEER_PREKEYS_MISSING')) throw e;
        res = await sendEncryptedMessage({
          ...sendArgs,
          recipient: { uid: found.uid, pubKeyBox: found.pubKeyBox },
          mySecKeyBox: ctx.secKey,
          myBoxPub: ctx.pubKey,
        });
      }
      const chatId = res?.chatId || res?.id;
      if (chatId) navigate(`/chat/${chatId}`);
      else navigate('/inbox');
    } catch (err) {
      const code = err?.code || err?.details?.code;
      const msg = err?.message || 'errore invio';
      if (code === 'permission-denied' || /whitelist/i.test(msg)) {
        setSendErr('il destinatario non accetta sconosciuti (whitelist)');
      } else {
        setSendErr(`${msg}${code ? ` (${code})` : ''}`);
      }
    } finally {
      setSending(false);
    }
  }

  // Allega come PRIMO messaggio (immagine/video/file): crea la chat con l'allegato.
  async function onPickFile(e) {
    const file = e.target?.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || !found) return;
    setSendErr(null);
    if (!boxSec) { setSendErr('chiavi non inizializzate, ricarica'); return; }
    setAttaching(true);
    try {
      const ctx = senderCtx();
      let res;
      try {
        res = await sendFsAttachment({
          boxKeypair: { pub: ctx.pubKey, priv: ctx.secKey },
          recipientUid: found.uid,
          recipientSigil: found.sigil,
          senderSigil: ctx.sigil,
          file,
          senderTTL: ttl,
          myBoxPub: ctx.pubKey,
          mySecKeyBox: ctx.secKey,
          downloadable: attachDownloadable,
        });
      } catch (e) {
        if (!String(e?.message || '').includes('PEER_PREKEYS_MISSING')) throw e;
        res = await sendEncryptedAttachment({
          file,
          recipient: { uid: found.uid, pubKeyBox: found.pubKeyBox },
          mySecKeyBox: ctx.secKey,
          myBoxPub: ctx.pubKey,
          senderTTL: ttl,
          senderSigil: ctx.sigil,
          recipientSigil: found.sigil,
          downloadable: attachDownloadable,
        });
      }
      const chatId = res?.chatId || res?.id;
      navigate(chatId ? `/chat/${chatId}` : '/inbox');
    } catch (err) {
      const m = (err?.message || '').toUpperCase();
      if (m.includes('VIDEO_TOO_LONG')) setSendErr('video troppo lungo (max 60s)');
      else if (m.includes('TOO_LARGE')) setSendErr(`Dettaglio: ${err?.message || ''} · file=${file?.name || '?'} type=${file?.type || '?'} size=${file?.size ? (file.size/1048576).toFixed(2) : '?'}MB`);
      else if (/whitelist/i.test(err?.message || '')) setSendErr('il destinatario non accetta sconosciuti (whitelist)');
      else setSendErr(err?.message || 'invio allegato fallito');
    } finally {
      setAttaching(false);
    }
  }

  return (
    <Frame statusLeft="NEW_TRANSMISSION" statusRight="5G">
      <Header
        title="// Nuovo messaggio"
        crumb="◀ indietro"
        onCrumbClick={() => navigate('/inbox')}
      />

      {!found && (
        <div className="space-y-3">
          {derivedList.length > 0 && (
            <div>
              <label className="block text-[13px] text-ink-dim mb-1">&gt; scrivi come</label>
              <select
                value={asSigil} onChange={(e) => setAsSigil(e.target.value)}
                className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none"
              >
                <option value="">@{mySigil} (identità principale)</option>
                {derivedList.map(([lower, d]) => (
                  <option key={lower} value={lower}>@{d.sigil} (derivato)</option>
                ))}
              </select>
              {asSigil && (
                <p className="text-[12px] text-amber mt-1">
                  scriverai come @{derivedKeys[asSigil]?.sigil}: il destinatario vedrà questo, non @{mySigil}.
                </p>
              )}
            </div>
          )}

          {contacts && contacts.length > 0 && (
            <div className="space-y-1.5">
              <label className="block text-[13px] text-ink-dim">&gt; i tuoi contatti (tap per scrivere)</label>
              {contacts.map((c) => (
                <button
                  key={c.contactUid}
                  onClick={() => pickContact(c.sigil)}
                  className="w-full text-left border border-ink-dim/50 rounded-xl px-3 py-2.5 hover:border-ink hover:bg-ink/[0.04] transition-colors"
                >
                  <span className="text-ink text-[15px]">@{c.sigil}</span>
                  {c.viaInvite && <span className="text-[11px] text-ink-dim ml-2">· via invito</span>}
                </button>
              ))}
            </div>
          )}

          <p className="text-[16px] text-ink-dim">
            {contacts && contacts.length > 0
              ? 'oppure cerca un altro sigil:'
              : 'A chi vuoi scrivere? Inserisci il suo sigil (il suo nome su Nullum).'}
          </p>

          <form onSubmit={doSearch} className="space-y-2">
            <div>
              <label className="block text-[13px] text-ink-dim mb-1">
                &gt; SIGIL del destinatario
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none select-none text-[16px]">@</span>
                <input
                  type="text"
                  name="sigil"
                  autoComplete="off"
                  spellCheck="false"
                  value={sigilInput}
                  onChange={(e) => setSigilInput(e.target.value)}
                  placeholder="ilsuonome"
                  className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] pl-7 pr-3 py-2 focus:border-ink focus:outline-none"
                />
              </div>
              <p className="text-[13px] text-ink-dim mt-1">
                3-20 caratteri · lettere, numeri, _ · la @ è automatica
              </p>
            </div>

            {searchErr && (
              <Box variant="warn" className="text-[16px]">
                {searchErr}
              </Box>
            )}

            <Btn type="submit" disabled={searching || !sigilInput.trim()}>
              {searching ? '...' : 'Cerca →'}
            </Btn>
          </form>
        </div>
      )}

      {found && (
        <div className="space-y-3">
          <Box variant="success" className="text-[16px]">
            <p className="text-ink mb-1">✓ destinatario trovato</p>
            <p className="text-ink-dim">
              @<span className="text-ink text-glow">{found.sigil}</span>
            </p>
            {found.acceptMode === 'whitelist' && (
              <p className="text-amber text-[13px] mt-1">
                ⚠ whitelist — potrebbe rifiutare
              </p>
            )}
          </Box>

          <div>
            <label className="block text-[13px] text-ink-dim mb-1">
              &gt; MESSAGGIO ({text.length}/500)
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="..."
              className="w-full bg-black border border-ink-dim text-ink font-mono text-[15px] px-3 py-2 focus:border-ink focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-[13px] text-ink-dim mb-1">
              &gt; quanto resta visibile (dopo che lo apre)
            </label>
            <select
              value={ttl}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v === 86400) {
                  alert('Durata 24 ore.\n\nIl messaggio resta leggibile 24h dopo l\'apertura del destinatario, poi sparisce.\nPuoi annullarlo solo prima che lui lo apra.');
                } else if (v === -1) {
                  alert('Durata ∞ (Premium).\n\nIl messaggio non scadra\' mai. Diventa come una chat tradizionale.\nPuoi annullarlo solo prima della prima lettura del destinatario.');
                }
                setTtl(v);
              }}
              className="w-full bg-black border border-ink-dim text-ink font-mono text-[15px] px-3 py-2 focus:border-ink focus:outline-none"
            >
              {TTL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {sendErr && (
            <Box variant="warn" className="text-[16px]">
              {sendErr}
            </Box>
          )}

          <input ref={inputGalleryRef} type="file" accept="image/*,video/*" className="hidden" onChange={onPickFile} />
          <input ref={inputVideoLiveRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={onPickFile} />
          <input ref={inputAudioLiveRef} type="file" accept="audio/*" capture className="hidden" onChange={onPickFile} />
          <input ref={inputAnyFileRef} type="file" className="hidden" onChange={onPickFile} />

          {attachMenuOpen && (
            <div className="border border-ink-dim p-2 text-[14px]">
              <button
                onClick={() => setAttachDownloadable((v) => !v)}
                className={`w-full mb-2 px-2 py-2 border text-left ${attachDownloadable ? 'border-emerald-500 text-emerald-400' : 'border-ink-dim text-ink-dim'}`}
              >
                {attachDownloadable ? '📥 Scaricabile: ON' : '👁 Solo visione (effimero)'}
                <div className="text-[11px] text-ink-dim mt-0.5">
                  {attachDownloadable ? 'salvabile, ma sparisce comunque dopo il TTL scelto' : 'one-view, si brucia dopo la visione'}
                </div>
              </button>
              {attachDownloadable && (
                <div className="mb-2 flex items-center gap-2 text-[12px] text-ink-dim">
                  <span>Durata (TTL):</span>
                  <select
                    value={ttl}
                    onChange={(e) => setTtl(Number(e.target.value))}
                    className="bg-black border border-ink-dim text-ink font-mono text-[13px] px-2 py-1 focus:border-ink focus:outline-none"
                  >
                    {TTL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { setAttachMenuOpen(false); inputGalleryRef.current?.click(); }}
                  className="border border-ink-dim text-ink px-2 py-2 hover:border-ink text-left">🖼 Foto / video da galleria</button>
                <button onClick={() => { setAttachMenuOpen(false); inputVideoLiveRef.current?.click(); }}
                  className="border border-ink-dim text-ink px-2 py-2 hover:border-ink text-left">🎥 Registra video ora</button>
                <button onClick={() => { setAttachMenuOpen(false); inputAudioLiveRef.current?.click(); }}
                  className="border border-ink-dim text-ink px-2 py-2 hover:border-ink text-left">🎙 Registra audio</button>
                <button onClick={() => { setAttachMenuOpen(false); inputAnyFileRef.current?.click(); }}
                  className="border border-ink-dim text-ink px-2 py-2 hover:border-ink text-left">📁 File qualsiasi</button>
                <button onClick={() => setAttachMenuOpen(false)}
                  className="col-span-2 text-[12px] text-ink-dim hover:text-ink py-1">annulla</button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => setAttachMenuOpen((v) => !v)} disabled={attaching}>
              {attaching ? '…' : '📎 Allega'}
            </Btn>
            <Btn onClick={doSend} disabled={sending || !text.trim() || attaching}>
              {sending ? '...' : 'Invia ▶'}
            </Btn>
          </div>
          <Btn variant="ghost" onClick={() => setFound(null)}>
            ◀ Cambia destinatario
          </Btn>
        </div>
      )}

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>
          Annulla
        </Btn>
      </Footer>
    </Frame>
  );
}
